import React, { useEffect, useRef, useState } from 'react';
import { useAd } from '../../contexts/AdContext';

interface PropellerAdProps {
  zoneId?: string;
  className?: string;
  style?: React.CSSProperties;
}

declare global {
  interface Window {
    propellerads?: {
      cmd: any[];
      push: (command: any) => void;
    };
  }
}

export const PropellerAd: React.FC<PropellerAdProps> = ({ 
  zoneId, 
  className = '', 
  style = {} 
}) => {
  const { adConfig, shouldShowAd, trackAdImpression, trackAdClick } = useAd();
  const adRef = useRef<HTMLDivElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [adZoneId] = useState(zoneId || adConfig.propellerZoneId);

  useEffect(() => {
    // Remove any previous injected scripts
    const prevScripts = document.querySelectorAll('[data-propeller-vignette]');
    prevScripts.forEach(s => s.remove());

    if (!shouldShowAd() || !adConfig.enabled) return;

    // 1. Inject the obfuscated inline script
    const inlineScript = document.createElement('script');
    inlineScript.setAttribute('data-cfasync', 'false');
    inlineScript.setAttribute('type', 'text/javascript');
    inlineScript.setAttribute('data-propeller-vignette', 'true');
    inlineScript.innerHTML = `
      (function(d,z,s,c){
        s.src='//'+d+'/400/'+z;
        s.onerror=s.onload=E;
        function E(){c&&c();c=null}
        try{(document.body||document.documentElement).appendChild(s)}catch(e){E()}
      })("oamoameevee.net",9532323,document.createElement('script'),function(){});
    `;
    document.body.appendChild(inlineScript);

    // 2. Inject the dynamic loader script
    const loaderScript = document.createElement('script');
    loaderScript.setAttribute('data-propeller-vignette', 'true');
    loaderScript.innerHTML = `
      (function(d,z,s,c){
        s.src='//'+d+'/400/'+z;
        s.onerror=s.onload=E;
        function E(){c&&c();c=null}
        try{(document.body||document.documentElement).appendChild(s)}catch(e){E()}
      })("oamoameevee.net",9532323,document.createElement('script'),function(){});
    `;
    document.body.appendChild(loaderScript);

    setIsLoaded(true);

    return () => {
      inlineScript.remove();
      loaderScript.remove();
    };
  }, [adConfig.enabled, shouldShowAd]);

  useEffect(() => {
    if (!shouldShowAd() || !adConfig.enabled) {
      return;
    }

    const loadAd = () => {
      if (!adRef.current || !window.propellerads) return;

      try {
        // Clear previous ad
        if (adRef.current) {
          adRef.current.innerHTML = '';
        }

        // Create ad container
        const adContainer = document.createElement('div');
        adContainer.id = `propeller-ad-${Date.now()}`;
        adContainer.className = 'propeller-ad-container';
        adRef.current.appendChild(adContainer);

        // Configure and show ad
        window.propellerads.push({
          site_id: adZoneId,
          container: adContainer.id,
          type: 'banner',
          test: adConfig.testMode
        });

        // Track impression
        trackAdImpression(adZoneId);
        setIsLoaded(true);

        // Add click tracking
        adContainer.addEventListener('click', () => {
          trackAdClick(adZoneId);
        });

      } catch (error) {
        console.error('Error loading PropellerAd:', error);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(loadAd, 100);
    return () => clearTimeout(timer);
  }, [adConfig.enabled, adConfig.testMode, adZoneId, shouldShowAd, trackAdImpression, trackAdClick]);

  if (!shouldShowAd() || !adConfig.enabled) {
    return null;
  }

  return (
    <div 
      ref={adRef}
      className={`propeller-ad-wrapper ${className}`}
      style={{
        minHeight: '90px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '8px',
        ...style
      }}
    >
      {!isLoaded && (
        <div className="text-gray-500 text-sm">
          Loading advertisement...
        </div>
      )}
    </div>
  );
}; 